import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, FileText, Download, Upload, CheckCircle2, XCircle } from 'lucide-react';
import { publicDevisApi } from '../../lib/api';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';

interface DevisInfo {
    company_name: string;
    logo_url: string | null;
    brand_color: string;
    label: string;
    montant: string;
    file_name: string;
    has_document: boolean;
    /** en_attente : le client peut agir. recu / decline / clos : la page ne fait plus qu'informer. */
    etat: 'en_attente' | 'recu' | 'decline' | 'clos';
    received_at: string | null;
    lost_reason: string;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; }
};

/**
 * La page que le client ouvre depuis le bouton de son email de devis.
 *
 * Aucune authentification, aucun compte : le client n'en a pas. Même famille que la page de confirmation
 * du fournisseur (`SupplierOrderConfirmPage`), dont c'est le gabarit — jeton opaque dans l'adresse, un
 * appel qui décrit quoi afficher, une ou deux actions, et rien d'autre.
 *
 * Elle fait deux choses, et c'est délibérément tout : redéposer le devis signé, ou dire qu'on ne donne pas
 * suite. Le second bouton n'est pas un ornement — sans lui, un client qui décline ne dit rien, le dossier
 * reste ouvert deux semaines avant d'apparaître en relance, et le motif de la perte est perdu pour le
 * bilan commercial.
 *
 * Ce que cette page NE fait PAS : faire avancer le dossier au jalon « Devis signé ». Le dépôt est
 * enregistré et signalé à l'établissement, mais c'est un humain qui ouvre le document et franchit le
 * jalon. Décision d'Emeni (2026-08-26).
 */
export const DevisSignaturePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<DevisInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [fichier, setFichier] = React.useState<File | null>(null);
    const [envoi, setEnvoi] = React.useState(false);
    const [etat, setEtat] = React.useState<DevisInfo['etat'] | null>(null);
    const [refusOuvert, setRefusOuvert] = React.useState(false);
    const [motif, setMotif] = React.useState('');

    React.useEffect(() => {
        if (!token) return;
        publicDevisApi.getInfo(token)
            .then((d: any) => { setInfo(d); setEtat(d?.etat || null); })
            .catch((e: any) => setErreur(e?.message || 'Lien invalide ou expiré.'))
            .finally(() => setLoading(false));
    }, [token]);

    const deposer = async () => {
        if (!token || !fichier) return;
        setEnvoi(true);
        setErreur('');
        try {
            // Lu en base64 côté navigateur, comme tous les autres dépôts de l'application.
            const data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                reader.readAsDataURL(fichier);
            });
            await publicDevisApi.uploadSigned(token, {
                file_name: fichier.name,
                mime_type: fichier.type || 'application/pdf',
                data,
            });
            setEtat('recu');
        } catch (e: any) {
            setErreur(e?.message || "Votre document n'a pas pu être envoyé.");
        } finally {
            setEnvoi(false);
        }
    };

    const decliner = async () => {
        if (!token || !motif.trim()) return;
        setEnvoi(true);
        setErreur('');
        try {
            await publicDevisApi.decline(token, motif.trim());
            setEtat('decline');
        } catch (e: any) {
            setErreur(e?.message || "Votre réponse n'a pas pu être enregistrée.");
        } finally {
            setEnvoi(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (!info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{erreur || "Ce lien n'est plus valide."}</p>
            </div>
        );
    }

    const logo = resolveLogoUrl(info.logo_url);
    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4">
            <div className="max-w-lg mx-auto space-y-5">
                <div className="text-center space-y-2">
                    {logo && <img src={logo} alt={info.company_name} className="h-14 mx-auto object-contain" />}
                    <h1 className="text-xl font-black text-content">{info.company_name}</h1>
                </div>

                <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Devis de privatisation</p>
                        <p className="text-lg font-bold text-content mt-0.5">{info.label}</p>
                        {info.montant && <p className="text-sm text-content-muted">Montant : <span className="font-bold text-content">{info.montant}</span></p>}
                    </div>

                    {info.has_document && (
                        <a href={`/api/devis/${token}/document`} target="_blank" rel="noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border hover:border-brand-500/50 text-sm font-bold text-content transition-colors">
                            <Download size={15} /> Télécharger le devis
                        </a>
                    )}

                    {etat === 'recu' ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                            <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-content">Votre devis signé nous est bien parvenu.</p>
                                <p className="text-xs text-content-muted mt-0.5">
                                    {info.received_at ? `Reçu le ${fmtDate(info.received_at)}. ` : ''}
                                    Nous revenons vers vous très vite. Rien d'autre à faire de votre côté.
                                </p>
                            </div>
                        </div>
                    ) : etat === 'decline' ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border">
                            <XCircle size={18} className="text-content-muted shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-content">Nous avons bien noté que vous ne donniez pas suite.</p>
                                <p className="text-xs text-content-muted mt-0.5">Merci de nous l'avoir dit — et à une prochaine fois.</p>
                            </div>
                        </div>
                    ) : etat === 'clos' ? (
                        <p className="text-sm text-content-muted">
                            Ce devis n'attend plus de réponse de votre part. Contactez votre interlocuteur si vous
                            avez une question.
                        </p>
                    ) : (
                        <>
                            {/* Le dépôt. PDF ou photo : beaucoup de clients signent sur papier et photographient
                                la page plutôt que de rescanner tout le document. */}
                            <div className="space-y-2 pt-1">
                                <p className="text-xs font-bold text-content">Déposer votre exemplaire signé</p>
                                <label className="block cursor-pointer">
                                    <input type="file" accept="application/pdf,image/*" className="hidden"
                                        onChange={(e) => setFichier(e.target.files?.[0] || null)} />
                                    <div className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-1 transition-colors ${fichier ? 'border-brand-500/60 bg-brand-500/5' : 'border-border hover:border-brand-500/40'}`}>
                                        {fichier ? (
                                            <>
                                                <FileText size={22} className="text-brand-600 dark:text-brand-400" />
                                                <p className="text-xs font-bold text-content">{fichier.name}</p>
                                                <p className="text-[10px] text-content-muted">{(fichier.size / 1024).toFixed(0)} Ko</p>
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={22} className="text-content-muted" />
                                                <p className="text-xs font-bold text-content">Choisir le fichier</p>
                                                <p className="text-[10px] text-content-muted">PDF ou photo — 10 Mo maximum</p>
                                            </>
                                        )}
                                    </div>
                                </label>
                                <button type="button" onClick={deposer} disabled={!fichier || envoi}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                                    {envoi ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Envoyer mon devis signé
                                </button>
                            </div>

                            <div className="pt-2 border-t border-border">
                                {!refusOuvert ? (
                                    <button type="button" onClick={() => setRefusOuvert(true)}
                                        className="w-full text-xs text-content-muted hover:text-content underline">
                                        Je ne donne pas suite
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-xs font-bold text-content">Pouvez-vous nous dire pourquoi ?</p>
                                        <p className="text-[11px] text-content-muted">
                                            Une phrase suffit. Cela nous aide vraiment à nous améliorer.
                                        </p>
                                        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3}
                                            placeholder="Budget, date indisponible, autre prestataire..." className={`${champ} resize-none`} />
                                        <div className="flex gap-2">
                                            <button type="button" onClick={() => { setRefusOuvert(false); setMotif(''); }} disabled={envoi}
                                                className="flex-1 px-3 py-2 rounded-xl border border-border text-xs font-bold text-content-muted hover:text-content">
                                                Annuler
                                            </button>
                                            <button type="button" onClick={decliner} disabled={!motif.trim() || envoi}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-border text-xs font-bold text-content hover:bg-surface-soft disabled:opacity-50">
                                                {envoi ? <Loader2 size={13} className="animate-spin" /> : null} Envoyer ma réponse
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
                </div>

                <p className="text-[10px] text-content-muted text-center">
                    Ce lien vous est personnel. Ne le transmettez pas.
                </p>
            </div>
        </div>
    );
};
